import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import { PrescriptionPadStep } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import { appendDosageSuffix } from '@/utils/dosageLimit/dosageSuffix';
import { getDosagePrefill } from '@/utils/dosageLimit/getDosagePrefill';
import { getFormulationDispenseLimitRows } from '@/utils/dosageLimit/getFormulationDispenseLimitRows';
import { getFormulationFilter } from '@/utils/dosageLimit/getFormulationFilter';
import { getPadItemMaxDailyDosePhrase } from '@/utils/dosageLimit/getMaxDailyDosePhrase';
import type { StateCreator } from 'zustand';
import type { PrescriptionAssistantSliceType, PrescriptionPadRules } from '../types';
import { getMedicationPadItemAlertError } from '../utils/getMedicationPadItemAlertError';
import { setRevalidatedPad } from '../utils/setRevalidatedPad';
import { syncDosageSuffixToStep } from '../utils/syncDosageSuffixToStep';
import { validateMaxDailyDose } from '../utils/validateMaxDailyDose';

/**
 * The item's dosage as the new dispense limits rule would have prefilled it: going off writes the maximum daily dose
 * back into the dosage text, the only way it reaches the pharmacy with the input hidden; coming on splits it out.
 * A ";" typed at the end of the dosage reads as the phrase's separator, so going off and on again turns "take one;"
 * into "take one".
 */
const withDispenseLimitsDosage = (item: MedicationPadItemType, rules: PrescriptionPadRules): MedicationPadItemType => {
  const detail = item.medicationPadItem;
  if (!detail) {
    return item;
  }
  if (!rules.enableDispenseLimits) {
    // Only a valid dose is written: with dispense limits off nothing flags an invalid one before it reaches the pharmacy
    const phrase = validateMaxDailyDose(detail.maxDailyDose, rules).hasError
      ? undefined
      : getPadItemMaxDailyDosePhrase(item);
    return {
      ...item,
      medicationPadItem: {
        ...detail,
        dosage: phrase ? appendDosageSuffix(detail.dosage, phrase) : detail.dosage,
        maxDailyDose: undefined,
      },
    };
  }
  const prefill = getDosagePrefill(detail.dosage, getFormulationFilter(item.filters), rules, {
    source: 'pad',
    productId: detail.productId,
  });
  // The cleaned text is taken even without a dose phrase in it, so a monthly dispense limit clause the prefill strips
  // comes out, and the item keeps the dose it already has
  return prefill.dosage === detail.dosage && prefill.maxDailyDose === undefined
    ? item
    : {
        ...item,
        medicationPadItem: {
          ...detail,
          dosage: prefill.dosage,
          maxDailyDose: prefill.maxDailyDose ?? detail.maxDailyDose,
        },
      };
};

/**
 * Feature flags resolve after the pad may already hold items, and stream live, so every item is re-validated under
 * the new rules, and its dosage follows a change to dispense limits.
 */
export const updatePrescriptionPadRules: StateCreator<
  PrescriptionAssistantSliceType,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  (rules: PrescriptionPadRules) => void
> = (set, get) => (rules) => {
  const { prescriptionPadRules, prescriptionPad } = get().prescriptionAssistant;
  // The pad wrapper pushes the rules on every mount; the same rules leave every item as it was validated
  if (
    prescriptionPadRules.enableDispenseLimits === rules.enableDispenseLimits &&
    prescriptionPadRules.requireMaxDailyDose === rules.requireMaxDailyDose
  ) {
    return;
  }
  const hasDispenseLimitsChanged = prescriptionPadRules.enableDispenseLimits !== rules.enableDispenseLimits;
  // A rules change moves no quantity, interval or limit, so these rows also stand after it
  const dispenseLimitRows = getFormulationDispenseLimitRows(
    prescriptionPad.medicationPadItems,
    prescriptionPad.dispenseLimits,
  );
  // At the review step the written phrases come out under the old rules and go back in under the new ones
  const withNewDispenseLimits = (item: MedicationPadItemType) => {
    if (prescriptionPad.step !== PrescriptionPadStep.Confirm) {
      return withDispenseLimitsDosage(item, rules);
    }
    const editedItem = syncDosageSuffixToStep(
      item,
      dispenseLimitRows,
      prescriptionPadRules,
      PrescriptionPadStep.Create,
    );
    return syncDosageSuffixToStep(
      withDispenseLimitsDosage(editedItem, rules),
      dispenseLimitRows,
      rules,
      PrescriptionPadStep.Confirm,
    );
  };
  const medicationPadItems = hasDispenseLimitsChanged
    ? prescriptionPad.medicationPadItems.map(withNewDispenseLimits)
    : prescriptionPad.medicationPadItems;
  // The review step shows no item alerts, so a new one there would disable submit without a reason. The pad goes back
  // to Create instead, where the alert shows beside the item
  const rowsUnderRules = rules.enableDispenseLimits ? dispenseLimitRows : [];
  const isRaisingAlertAtReview =
    prescriptionPad.step === PrescriptionPadStep.Confirm &&
    medicationPadItems.some(
      (item, index) =>
        !prescriptionPad.medicationPadItems[index]?.alertError &&
        getMedicationPadItemAlertError(item, rowsUnderRules, rules),
    );
  setRevalidatedPad(
    set,
    {
      ...prescriptionPad,
      medicationPadItems: isRaisingAlertAtReview
        ? medicationPadItems.map((item) =>
            syncDosageSuffixToStep(item, dispenseLimitRows, rules, PrescriptionPadStep.Create),
          )
        : medicationPadItems,
    },
    rules,
    'prescriptionPad/updatePrescriptionPadRules',
    (state) => {
      state.prescriptionAssistant.prescriptionPadRules = rules;
      if (isRaisingAlertAtReview) {
        state.prescriptionAssistant.prescriptionPad.step = PrescriptionPadStep.Create;
      }
    },
    dispenseLimitRows,
  );
};
